// 頁型模板①：一般 CRUD 清單（53 筆 route）。
//
// 原系統對應（代表頁 `WEB-INF/views/instruction/courseDepartmentCode.jsp`）：
//   :27-29   `.ibox-title > h5` 頁面標題            → <ContentPanel title>
//   :33      `<button id="addBtn" class="btn btn-add">新增…</button>`
//                                                   → <SearchPanel extraActions>
//   :36-56   查詢欄位 + `#queryBtn`「查詢」          → <SearchPanel>
//   :80-90   `<table class="table table-bordered">` + DataTables
//                                                   → <DataTableShell> + <Table>
//   :98-105  `#dialogModal`（modal-lg，標題「新增／修改 XXX」，js:89,91）
//                                                   → <ModalFrame>
//   :163-167 modal-footer「關閉」+「儲存」
//   刪除確認：js-my/class/announcementModal.js:40 `sysConfirm("是否確定要刪除?")`
//                                                   → useDialogs().confirm
//
// 支援的 scenario（NEW-UI-IMPLEMENTATION-CHECKLIST §5.2）：
//   default / empty / create / edit / confirmation / success / validation-error
// 另外三個是所有頁型共通的資料狀態，不在預設集內但一定要正確：
//   loading（DataTableShell 的處理中遮罩）／ error ／ unauthorized（ErrorFrame）
//
// 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】

import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { FormMessage } from '@/components/base/FormMessage';
import { LINK_TEXT_CLASS } from '@/components/base/shared-classes';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { formSpecFor } from '@/mocks/legacy-form-specs';
import { LegacyAsideBlocks } from '@/mocks/legacy-aside-notes';
import {
  ContentPanel,
  DataTableShell,
  FormGrid,
  FormGridField,
  ModalFrame,
  SearchPanel,
  useDialogs,
} from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import type { LegacyModalSpec } from '@/mocks/legacy-modals.generated';
import { t } from '@/messages/zh-TW';
import {
  columnsFor,
  DEFAULT_PAGE_SIZE,
  initialValues,
  legacyContractFieldsFor,
  legacyTableLayoutFor,
  legacyTableSequenceFor,
  type Row,
  resolveLegacyMessage,
  resolveLegacyMessageForAction,
  STATUS_ENABLED,
  toPayload,
  useDataSource,
  useList,
  useMessageKeyOverride,
  useScenario,
} from '@/mocks';
import { LegacyContractFields } from './LegacyContractFields';
import {
  deleteWithUsageCheck,
  legacyAlertFallback,
  pageBehavior,
  type BehaviorFlowContext,
  type BehaviorSelectionApi,
} from './page-behaviors';
import {
  DialogHost,
  ExtraValidationMessages,
  extraMessagesOf,
  FieldControl,
  fieldErrorsOf,
  rowUiKey,
  TemplateError,
  toFormValues,
  toUiKey,
  type PageColumn,
} from './shared';
import type { PageTemplateProps } from './types';

/** 表格最後一欄（修改／刪除）的欄位 key。四種 fixture shape 都沒有叫 actions 的欄位。 */
const ACTIONS_COLUMN = 'actions';

interface EditingState {
  mode: 'create' | 'edit';
  /** create 時為 null。 */
  id: string | null;
  /** create 時為 null；edit 時保留原列（modal 唯讀判定與標題用）。 */
  row: Row | null;
  values: Record<string, string>;
}

export function CrudListPage({ route }: PageTemplateProps): ReactElement {
  return (
    <DialogHost route={route}>
      <CrudListBody route={route} />
    </DialogHost>
  );
}

function CrudListBody({ route }: PageTemplateProps) {
  const pageId = route.pageId;
  const navigate = useNavigate();
  // 批次 16（B8-2，Gitea issue #15）：crud-list 頁型的整頁自訂 body（profile-profile-maintenance：
  // 單筆表單＋證照子清單，泛用查詢＋列表模板不匹配）。批次 13 complexBody hook 先例
  // （ComplexFormPage 同款早退），放在 hooks 之前——本頁不需要任何模板狀態，
  // 自訂 body 自行取資料。未登錄頁零影響。
  const complexBehavior = pageBehavior(pageId);
  // 批次 19（B8-1，issue #14）：dashboard 頁型的整頁自訂 body（main-* 首頁 8 變體：
  // 契約 0 欄、menuRow＋公告欄＋訓練資訊 panel，泛用查詢＋列表模板完全不匹配）。
  // complexBody hook 同款早退（批次 16/17 先例）——放在 hooks 之前，未登錄頁零影響。
  if (complexBehavior?.dashboardBody !== undefined) {
    return complexBehavior.dashboardBody({ pageId });
  }
  if (complexBehavior?.complexBody !== undefined) {
    return complexBehavior.complexBody({ pageId });
  }
  const scenario = useScenario();
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // FC-5 R4（C1-c‴）：同頁同分類多條訊息碰撞時，注入哪一條就顯示哪一條（沒注入時
  // undefined，所有呼叫端退回 R3 既有的自動挑選，行為不變）。
  const messageKeyOverride = useMessageKeyOverride();
  // FC-5 R2（C1-c′）：這頁專屬的空表文案（沒有就是 null，DataTableShell 退回雛形預設）。
  const legacyEmptyMessage = resolveLegacyMessage(pageId, 'empty', messageKeyOverride);
  const columns = columnsFor(pageId);
  const layout = legacyTableLayoutFor(pageId, columns);
  // FC-5 R3：這頁是不是「啟用/停用」欄位池——原系統很多清單頁除了修改／刪除，
  // 還有一顆依目前狀態顯示「啟用」或「停用」的切換鈕（走既有的 dataSource.update()，
  // 見下方 requestToggleStatus()）。只有這個欄位池的頁面才畫這顆鈕，不是每頁都加。
  const statusColumn = columns.find(
    (c) => c.kind === 'status' && JSON.stringify(c.pool) === JSON.stringify(STATUS_ENABLED),
  );

  // FC-3：原系統該頁主體區的實測欄位（含 name／type／required／hidden／static options）。
  // legacySearchGroupsFor() 的退路（只涵蓋第一個查詢 form 的舊路徑）已刪除——
  // legacy-fallback-dead-path.test.ts 逐頁驗證過全站 156 頁沒有一頁「有 searchGroups
  // 卻沒有 contractFields」，那個分支從未被渲染過。
  const contractFields = legacyContractFieldsFor(pageId);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  /** 已套用的關鍵字（送進 DataSource 的查詢條件）。 */
  const [keyword, setKeyword] = useState('');
  /** 查詢表單裡還沒按下「查詢」的草稿值。 */
  const [draftKeyword, setDraftKeyword] = useState('');
  /** 契約欄位的草稿值（還沒按「查詢」）。 */
  const [draftFieldValues, setDraftFieldValues] = useState<Record<string, string>>(() =>
    initialValues(contractFields ?? [], pageId),
  );
  /** 已套用的契約欄位條件，真的會進 `dataSource.list()` 的 `query.filters`（criteria C5-h）。 */
  const [filters, setFilters] = useState<Record<string, string>>({});
  // 批次 22（B2-2）：查詢鈕版本號。行事曆檢視中按「查詢」要切回列表
  // （legacy #btnQuery 把 #display 設回 table，clazzList_js.jsp:162-165）。
  const [searchVersion, setSearchVersion] = useState(0);

  const { data, loading, error, reload } = useList(pageId, { page, pageSize, keyword, filters });
  const rows = data?.rows ?? [];

  const [editing, setEditing] = useState<EditingState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // FC-5 R3：這頁 validation 訊息數超過可用欄位數時，多出來的訊息（多槽位顯示）。
  const [extraErrors, setExtraErrors] = useState<readonly string[]>([]);
  // success 狀態：scenario 直接指定，或真的存檔成功之後。
  const [saved, setSaved] = useState(scenario === 'success');
  // crud-fix（2026-10-04）：存檔成功後的第二段 modal（qb 兩段式誠實殼；postSaveModal hook）。
  const [postSave, setPostSave] = useState<{ title: string; note: string } | null>(null);

  function applyKeyword(next: string) {
    setKeyword(next);
    setDraftKeyword(next);
    setPage(1);
  }

  /** 按下「查詢」：草稿值成為真正送進 DataSource 的 `filters`。 */
  function applySearch() {
    setKeyword(draftKeyword);
    setFilters(toPayload(draftFieldValues, contractFields, pageId));
    setPage(1);
    // 批次 22（B2-2）：版本號遞增（行事曆檢視中按「查詢」＝切回列表，clazzList_js.jsp:162-165）。
    setSearchVersion((version) => version + 1);
  }

  /**
   * 批次 22（B2-2）：把值合併寫回查詢表單草稿並重查。行事曆 viewRender 的鏡像：
   * 月檢視起訖寫回 #beginDate/#endDate 後 queryClassAndSetCalendar
   * （clazzList_js.jsp:2862-2868／classUtils.js:340-346 month 分支）。
   */
  function applyQueryValues(values: Record<string, string>) {
    const merged = { ...draftFieldValues, ...values };
    setDraftFieldValues(merged);
    setFilters(toPayload(merged, contractFields, pageId));
    setPage(1);
  }

  function resetSearch() {
    setDraftKeyword('');
    setKeyword('');
    setDraftFieldValues(initialValues(contractFields ?? [], pageId));
    setFilters({});
    setPage(1);
  }

  function openCreate(preset?: Record<string, string>) {
    setFieldErrors({});
    setExtraErrors([]);
    const base = toFormValues(formColumnsFor('create'), null);
    setEditing({
      mode: 'create',
      id: null,
      row: null,
      values: preset === undefined ? base : { ...base, ...preset },
    });
  }

  function openEdit(row: Row, preset?: Record<string, string>) {
    setFieldErrors({});
    setExtraErrors([]);
    const base = toFormValues(formColumnsFor('edit'), row);
    setEditing({
      mode: 'edit',
      id: String(row.id ?? ''),
      row,
      // 批次 12（B1-4）：preset 讓入口來源能傳給自訂 modal（樹狀圖 editEntry='tree'——
      // legacy 樹入口 showModal 無停用鎖（:41-136 無 isEnable 分支），列表入口
      // showTableModal 才有（:134-145）。
      values: preset === undefined ? base : { ...base, ...preset },
    });
  }

  /**
   * 批次 1：modal 表單欄位。行為層可換掉欄位集合（annual：年度／系統預設年度／排序序號，
   * 不含狀態欄）；沒有行為層的頁沿用表格欄（原行為）。modal 的值初始化也用同一組欄位，
   * 行為層加的 pseudo 欄位（role 的系統/流程控制標記）才拿得到種子值。
   */
  function formColumnsFor(mode: 'create' | 'edit'): readonly PageColumn[] {
    return behavior?.modalColumns?.(columns, mode) ?? columns;
  }

  async function persist(state: EditingState) {
    setFieldErrors({});
    setExtraErrors([]);
    try {
      await dialogs.processing.wrap(
        state.mode === 'create'
          ? dataSource.create(pageId, state.values)
          : dataSource.update(pageId, state.id ?? '', state.values),
      );
      setEditing(null);
      setSaved(true);
      reload();
      // FC-5 R2（C1-c′）：這頁若有 legacy 的 success 訊息就顯示它，否則退回雛形通用文案。
      await dialogs.info(resolveLegacyMessage(pageId, 'success')?.text ?? t('state.saveSuccess'));
      // crud-fix（2026-10-04）：存檔成功後的第二段 modal（qb 兩段式誠實殼；未登錄行為＝不開）。
      // legacy nextBtn=save(0) 成功即 showInitImportModal()（questionBank.jsp:139-146，無成功
      // 提示）；雛形保留既有「成功提示 → 第二步」順序（既有 persist 提示面不動），順序差註解記錄。
      const spec = behavior?.postSaveModal?.({ mode: state.mode, id: state.id ?? '', row: state.row });
      if (spec !== undefined) {
        setPostSave(spec);
      }
    } catch (cause: unknown) {
      const fields = fieldErrorsOf(cause);
      setFieldErrors(fields);
      // FC-5 R3：欄位數不夠裝的 legacy validation 訊息，另外顯示成一份清單（多槽位顯示）。
      setExtraErrors(extraMessagesOf(cause));
      // 沒有欄位級訊息（例如 unauthorized）時退回原系統的通用 alert（errorContent.jsp:5）。
      // FC-5 R3：這是新增／修改動作，動作關鍵字用 'save'。
      // 批次 1：resource-rules 的業務規則衝突（例：角色重複）帶 legacy 原文，優先顯示。
      if (Object.keys(fields).length === 0) {
        await dialogs.alert(
          resolveLegacyMessageForAction(pageId, 'failure', 'save', messageKeyOverride)?.text ??
            legacyAlertFallback(cause),
        );
      }
    }
  }

  /**
   * 匯出：D-1 第 2a 點規定 `list:<resource>#export` 對應
   * `dataSource.list(resource, { ...query, export: true })`。
   * 雛形不引 DataTables／jszip（§3.2），拿到資料後仍只跳一則說明；
   * 但**必須真的打資料源**，否則就是 criteria C5-j 要擋的假動作。
   */
  async function exportRows() {
    try {
      await dataSource.list(pageId, { page, pageSize, keyword, filters, export: true });
    } catch {
      // FC-5 R3：匯出失敗用它自己專屬的動作關鍵字 'export'，不跟一般查詢/儲存失敗共用。
      await dialogs.alert(
        resolveLegacyMessageForAction(pageId, 'failure', 'export', messageKeyOverride)?.text ??
          t('error.generic'),
      );
      return;
    }
    await dialogs.info(t('export.notice'));
  }

  async function requestDelete(row: Row) {
    // FC-5 R3：刪除的 confirm/failure 都用動作關鍵字 'delete'，跟啟用/停用/儲存分開。
    // 批次 2：clazzList.jsp:366 的確認文字是全形問號「是否確定要刪除？」，行為層可覆寫。
    // 批次 3：覆寫可為函式（questionBank.jsp:365-368 依題數換確認文字）。
    const deleteConfirmText =
      typeof behavior?.deleteConfirm === 'function'
        ? behavior.deleteConfirm(row)
        : behavior?.deleteConfirm;
    const confirmed = await dialogs.confirm(
      deleteConfirmText ??
        resolveLegacyMessageForAction(pageId, 'confirm', 'delete', messageKeyOverride)?.text ??
        t('confirm.delete'),
    );
    if (!confirmed) {
      return;
    }
    try {
      await dialogs.processing.wrap(dataSource.remove(pageId, String(row.id ?? '')));
      reload();
    } catch {
      await dialogs.alert(
        resolveLegacyMessageForAction(pageId, 'failure', 'delete', messageKeyOverride)?.text ??
          t('error.generic'),
      );
    }
  }

  /**
   * FC-5 R3：狀態切換（啟用／停用）。只有 `statusColumn` 存在的頁面才會呼叫這個函式
   * （見 `rowActions()` 的按鈕條件渲染）。方向依這一列目前的狀態值決定——目前是「啟用」
   * 就問「是否確定要停用?」，反之問「是否確定要啟用?」，對齊原系統的切換鈕語意。
   */
  async function requestToggleStatus(row: Row) {
    if (statusColumn === undefined) return;
    const currentlyEnabled = String(row[statusColumn.key] ?? '') === '啟用';
    const action = currentlyEnabled ? 'disable' : 'enable';
    const fallback = action === 'disable' ? t('confirm.disable') : t('confirm.enable');
    const confirmed = await dialogs.confirm(
      resolveLegacyMessageForAction(pageId, 'confirm', action, messageKeyOverride)?.text ?? fallback,
    );
    if (!confirmed) {
      return;
    }
    try {
      // 走既有的 update()（不是另開的方法），dataEffect="update:<pageId>" 才是真話。
      await dialogs.processing.wrap(
        dataSource.update(pageId, String(row.id ?? ''), { [statusColumn.key]: action === 'enable' ? '啟用' : '停用' }),
      );
      reload();
    } catch {
      const failureFallback = action === 'disable' ? t('error.disableFailed') : t('error.enableFailed');
      await dialogs.alert(
        resolveLegacyMessageForAction(pageId, 'failure', action, messageKeyOverride)?.text ??
          failureFallback,
      );
    }
  }

  /**
   * 一列的行動作（修改／刪除）。
   *
   * FC-3：行動作**永不自己開一欄**（設計 B.4 第 1 點）。legacy 表頭序列有空字串欄時
   * 放在那一欄，沒有時放進第一個連結欄的儲存格。按鈕本身與 `data-effect` 一個都不刪，
   * 「修改」的顯示條件也**逐字沿用改版前**（`layout?.actionsFirst === true &&
   * layout.editKey !== null` 才隱藏），避免動到 FC-2 已相符的 actions 分子。
   */
  function rowActions(row: Row, key: string): ReactElement {
    return (
      <span data-ui-id={`${pageId}-result-row-${key}-actions`} className="inline-flex gap-1">
        {/*
          操作欄與名稱連結同時存在時，「修改」已經掛在名稱連結上（與原系統一致），
          這裡只留「刪除」——原系統那一欄本來就只有刪除連結
          （基準樹 ePortfolio_work/ePortfolio-tsgh/WebContent/WEB-INF/views/
          config/role.jsp:53-65）。沒有連結欄可掛的話才把修改留在這裡。
        */}
        {(layout?.actionsFirst === true && layout.editKey !== null) ||
        behavior?.suppressRowEdit === true ? null : (
          <Button
            uiId={`${pageId}-result-row-${key}-edit`}
            variant="default"
            size="sm"
            type="button"
            onClick={() => {
              openEdit(row);
            }}
          >
            {t('action.edit')}
          </Button>
        )}
        {(behavior?.deleteVisible !== undefined && !behavior.deleteVisible(row)) ? null : (
          <Button
            uiId={`${pageId}-result-row-${key}-delete`}
            variant="danger"
            size="sm"
            type="button"
            // D-1：這顆真的會打 `dataSource.remove(pageId, id)`（requestDelete）。
            // 同一列的「修改」只開 modal，不標（屬 modals 維度，criteria C5-j）。
            // 批次 1：annual／department／location 的刪除先走 usage check 流程
            // （先 get 取 usage 旗標、再 confirm／alert）——移除本身仍是 remove()。
            dataEffect={`remove:${pageId}`}
            onClick={() => {
              if (behavior?.usageCheck !== undefined) {
                void deleteWithUsageCheck(flowCtx, row, behavior.usageCheck);
              } else {
                void requestDelete(row);
              }
            }}
          >
            {t('action.delete')}
          </Button>
        )}
        {/*
          FC-5 R3：狀態切換鈕——只有「啟用/停用」欄位池的頁面才有（`statusColumn`）。
          原系統很多清單頁本來就有這顆鈕（雛形之前沒做），按鈕文字依目前狀態顯示
          「相反」的那個字（目前啟用就顯示「停用」，反之顯示「啟用」），對齊常見的
          切換鈕語意。
        */}
        {statusColumn === undefined ||
        (behavior?.toggleVisible !== undefined && !behavior.toggleVisible(row)) ? null : (
          <Button
            uiId={`${pageId}-result-row-${key}-toggle-status`}
            variant="default"
            size="sm"
            type="button"
            // D-1 的 5 個 op 沒有「狀態切換」這一種——語意上最接近 update（修改這筆資料的
            // 一個欄位），endpoint-map.ts 的 Op 型別是 D-1 凍結的既有規則，本批不新增第 6 種
            // op（criteria C3：抽取器與比對器判定不得動）。
            dataEffect={`update:${pageId}`}
            onClick={() => {
              void requestToggleStatus(row);
            }}
          >
            {String(row[statusColumn.key] ?? '') === '啟用' ? t('action.disable') : t('action.enable')}
          </Button>
        )}
        {/* 批次 2：額外列動作（開課管理：複製）。 */}
        {(behavior?.rowExtraActions !== undefined
          ? behavior.rowExtraActions(row, flowCtx)
          : []
        ).map((element, extraIndex) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: 額外動作清單是靜態的，位置即識別。
          <span key={`extra-${String(extraIndex)}`} className="contents">
            {element}
          </span>
        ))}
      </span>
    );
  }

  // ── scenario 預設集：把 create／edit／confirmation／validation-error 直接呈現出來 ──
  //
  // 這四個狀態在 DataSource 層看不出差別（回傳的資料一樣），差別在**畫面停在哪一步**。
  // scenario 是整頁層級的驗證開關（§16.2），所以由模板在資料就緒後自動走到那一步，
  // 截圖與 Playwright 才不必先模擬點擊。只跑一次，之後使用者的操作不會被蓋掉。
  const autoRan = useRef(false);
  // 相依陣列刻意不完整：把 columns／dataSource／dialogs 放進去，會讓這段在使用者操作後重跑，
  // 把已經打開的 modal 或已經按掉的確認框又叫回來。autoRan 保證整段只生效一次。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 見上方說明
  useEffect(() => {
    if (autoRan.current || loading || error !== null) {
      return;
    }
    const first = rows[0];
    if (scenario === 'create') {
      autoRan.current = true;
      openCreate();
      return;
    }
    if (first === undefined) {
      return;
    }
    if (scenario === 'edit') {
      autoRan.current = true;
      openEdit(first);
    } else if (scenario === 'validation-error') {
      autoRan.current = true;
      const state: EditingState = {
        mode: 'edit',
        id: String(first.id ?? ''),
        row: first,
        values: toFormValues(formColumnsFor('edit'), first),
      };
      setEditing(state);
      // 走真正的存檔路徑，欄位錯誤才會是 DataSource 回的那一份，不是另外寫死一份。
      void persist(state);
    } else if (scenario === 'confirmation') {
      autoRan.current = true;
      void requestDelete(first);
    }
  }, [scenario, loading, error, rows]);

  // 批次 1（config）：頁面行為註冊表。未登錄的頁回 undefined，以下所有分支都走原路徑。
  const behavior = pageBehavior(pageId);
  // W11（批次 42，issue #40，裁決④案 B）：legacy form spec（白名單制）。抽變數＝
  // exactOptionalPropertyTypes 的條件展開需要 TS 可 narrow（函式呼叫結果推不出非 undefined）。
  const formSpec = formSpecFor(pageId);
  // 批次 1：批量勾選狀態（帳號維護的停用/啟用勾選欄）。行為層未宣告 selectionBuckets 時不建。
  const [selectionState, setSelectionState] = useState<Record<string, ReadonlySet<string>>>({});
  // 批次 15（B7-1）：列展開狀態（legacy DataTable row.child；存**原始列鍵**，
  // 傳給 Table 時再映射成 rowKey 空間）。behavior.rowExpanded 未登錄頁零影響。
  const [expandedRowIds, setExpandedRowIds] = useState<ReadonlySet<string>>(new Set());
  const toggleRowExpand = (rowId: string) => {
    setExpandedRowIds((current) => {
      const next = new Set(current);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };
  const selectionApi: BehaviorSelectionApi | undefined =
    behavior?.selectionBuckets === undefined
      ? undefined
      : {
          has: (bucket, id) => selectionState[bucket]?.has(id) ?? false,
          toggle: (bucket, id) => {
            setSelectionState((current) => {
              const next: Record<string, ReadonlySet<string>> = { ...current };
              const set = new Set(next[bucket] ?? []);
              if (set.has(id)) {
                set.delete(id);
              } else {
                set.add(id);
              }
              next[bucket] = set;
              return next;
            });
          },
          ids: (bucket) => [...(selectionState[bucket] ?? [])],
          clear: (bucket) => {
            setSelectionState((current) => {
              if (bucket === undefined) {
                return {};
              }
              const next: Record<string, ReadonlySet<string>> = { ...current };
              delete next[bucket];
              return next;
            });
          },
        };
  // 批次 1：行為流程的執行環境（openCreate／openEdit 在下方宣告，函式宣告有提升，
  // 物件在 render 時才組，引用不會落到 undefined。
  const flowCtx: BehaviorFlowContext = {
    pageId,
    reload,
    openEdit,
    openCreate,
    dataSource,
    dialogs,
    ...(selectionApi === undefined ? {} : { selection: selectionApi }),
    // 批次 9：匯入流程讀查詢表單草稿值（org 選單的組織名稱、comment 的 uploadFile 檔名）。
    queryValues: draftFieldValues,
    // 批次 22（B2-2）：行事曆寫回查詢表單＋重查；查詢鈕版本號（切回列表用）。
    applyQueryValues,
    searchVersion,
    // 批次 15（B7-1）：列展開狀態（planInstList 明細／學習紀錄已填明細；rowExpanded 未登錄頁零影響）。
    expandedRowIds: expandedRowIds,
    toggleRowExpand: toggleRowExpand,
    // 批次 15 Wave 2（B7-2）：依列鍵取列（fillCross 對勾選列驗證）。
    rowsById: (id: string) => rows.find((candidate) => String(candidate.id ?? '') === id),
    // 批次 4：表單查詢雙頁的批量流程需要 done 旗標查詢與全選清單。
    ...(behavior?.selectionBuckets?.includes('form') === true
      ? {
          doneLookup: (id: string) =>
            String((data?.rows ?? []).find((row) => String(row.id) === id)?.['done'] ?? '') === '1',
          enabledLookup: (id: string) =>
            String((data?.rows ?? []).find((row) => String(row.id) === id)?.['enabled'] ?? '') === '1',
          allRowIds: () => (data?.rows ?? []).map((row) => String(row.id ?? '')),
        }
      : {}),
  };
  // FC-3：表頭序列 = 抽取序列，模板**不得增刪重排任何一欄**。
  //
  // 原系統把操作連結放在表頭為空字串的那一欄（多半是第一欄）。舊版 React 一律把
  // 「操作」接在最後，於是整張表差一欄、每一格都對到隔壁去（設計 A.5 的流程追蹤）。
  // 現在操作欄的位置由 legacy 表頭序列裡空字串欄的 index 決定；
  // **序列沒有空字串欄就沒有操作欄**——行動作改掛在第一個連結欄的儲存格上
  // （與原系統 `<a class="showEdit">` 一致，見下方 renderCell）。
  //
  // 沒有實測表頭序列的頁面（fixtures 退回 PageShape 的四種通用欄位）維持舊行為：
  // 那些頁在 legacy 契約裡本來就沒有表頭項，追加的「操作」欄只會計入 reactOnly。
  const sequence = legacyTableSequenceFor(pageId);
  const dataColumns = columns.map((column) => ({ key: column.key, header: column.label }));
  // 批次 44：第一欄（空字串欄）＝表頭全選掛點。
  const checkColumnKey = sequence?.find((column) => column.actions)?.key ?? 'actions';
  const tableColumns: readonly TableColumn[] =
    sequence === undefined
      ? [
          ...dataColumns,
          { key: ACTIONS_COLUMN, header: t('table.actions'), align: 'right' as const },
        ]
      : sequence.map((column) => ({
          key: column.key,
          header:
            // 批次 44（#42）：表頭全選（theadSelectAll 登錄頁；第一欄空字串欄；
            // leaveAnnual.jsp／planFormList.jsp:741 id=form-select-all）。
            column.key === checkColumnKey && behavior?.theadSelectAll !== undefined ? (
              <input
                type="checkbox"
                {...(typeof behavior.theadSelectAll === 'string' ? { id: behavior.theadSelectAll } : {})}
                data-ui-id={`${pageId}-form-select-all`}
                className="size-4 accent-brand"
                onChange={(event) => {
                  // 全選＝勾選本頁全部列（selectionApi 存在時才作用；未宣告 selectionBuckets
                  // 的頁＝樣貌鏡像，批次 44 誠實標註）。
                  if (selectionApi === undefined || rows.length === 0) return;
                  const bucket = behavior?.selectionBuckets?.[0];
                  if (bucket === undefined) return;
                  for (const row of rows) {
                    const id = String(row.id ?? '');
                    const has = selectionApi.has(bucket, id);
                    if (event.target.checked && !has) selectionApi.toggle(bucket, id);
                    if (!event.target.checked && has) selectionApi.toggle(bucket, id);
                  }
                }}
              />
            ) : (
              column.header
            ),
        }));
  /** 這一頁的操作欄 key（可能不只一個，也可能一個都沒有）。 */
  const actionsKeys = new Set(
    sequence === undefined ? [ACTIONS_COLUMN] : sequence.filter((c) => c.actions).map((c) => c.key),
  );
  /** 行動作按鈕畫在哪一欄；沒有操作欄時是 null（改掛在連結欄上）。 */
  const primaryActionsKey =
    sequence === undefined ? ACTIONS_COLUMN : (sequence.find((c) => c.primaryActions)?.key ?? null);
  /** 沒有操作欄可用時，「刪除」掛在第一個資料欄的儲存格裡（不另外開一欄）。 */
  const inlineActionsKey =
    primaryActionsKey === null ? (layout?.editKey ?? dataColumns[0]?.key ?? null) : null;

  // 批次 1：修改連結的欄位。legacy-tables 有形狀資料的頁用它的 linkKeys；
  // 行為層登錄了 editLinkKeys 的頁（annual 年度、role 角色名稱、location 地點名稱）用行為層的。
  const behaviorLinkKeys =
    behavior?.editLinkKeys !== undefined
      ? new Set<string>(behavior.editLinkKeys)
      : (layout?.linkKeys ?? new Set<string>());

  // 批次 15（B7-1）：列展開（legacy DataTable row.child）；先取區域變數避免 JSX 內的非空斷言。
  const rowExpandedHook = behavior?.rowExpanded;
  const expandedKeysForTable = rowExpandedHook
    ? new Set(
        rows
          .filter((candidate) => expandedRowIds.has(String(candidate.id ?? '')))
          .map((candidate, mapIndex) => rowUiKey(candidate, mapIndex)),
      )
    : new Set<string>();

  // 批次 1：資料表區塊抽成元素常數，行為層（department 的樹狀圖/列表頁籤）可以包一層容器。
  const resultShell = (
    <DataTableShell
      uiId={`${pageId}-result`}
      page={page}
      pageSize={pageSize}
      total={data?.total ?? 0}
      keyword={keyword}
      loading={loading}
      onPageChange={setPage}
      onPageSizeChange={(next) => {
        setPageSize(next);
        setPage(1);
      }}
      onKeywordChange={applyKeyword}
      // FC-5 R2（C1-c′）：這頁若有 legacy 的 empty 訊息就顯示它，否則退回 DataTableShell 預設。
      {...(legacyEmptyMessage === null ? {} : { emptyText: legacyEmptyMessage.text })}
      // D-1 第 2a 點：匯出走 `list:<resource>#export`，端點是該資源的下載端點，
      // 與查詢端點不同，所以 key 帶後綴。按鈕真的會呼叫 dataSource.list（criteria C5-j）。
      exportEffect={`list:${pageId}#export`}
      onExport={() => {
        void exportRows();
      }}
    >
      <Table
        uiId={`${pageId}-result-table`}
        columns={tableColumns}
        rows={rows}
        rowKey={rowUiKey}
        // 批次 15（B7-1）：列展開（legacy DataTable row.child）；rowExpandedHook 未登錄頁零影響。
        {...(rowExpandedHook === undefined
          ? {}
          : { expandedRowRender: (row: Record<string, unknown>) => rowExpandedHook(row, flowCtx) })}
        expandedKeys={expandedKeysForTable}
        // 空表文案由 DataTableShell 負責，這裡給空字串避免同一畫面出現兩次。
        emptyText=""
        renderCell={(row, columnKey) => {
          const key = toUiKey(String(row.id ?? ''));
          // 批次 1（config）：行為層的儲存格覆寫（annual 是否預設/狀態切換、
          // role 角色設定）。回 null 走預設渲染，不影響未登錄的頁。
          const customCell = behavior?.cellOverride?.(row, columnKey, flowCtx);
          if (customCell !== null && customCell !== undefined) {
            return customCell;
          }
          if (!actionsKeys.has(columnKey)) {
            const raw = row[columnKey];
            const value = raw === undefined || raw === null ? '' : String(raw);
            // 沒有操作欄的頁面：行動作掛在第一個連結欄的儲存格裡，不另外開一欄
            // （設計 B.4 第 1 點；原系統本來就把修改掛在名稱連結上）。
            const inlineActions =
              columnKey === inlineActionsKey ? rowActions(row, key) : null;
            // 原系統把這一欄的儲存格包成 <a>（例基準樹
            // ePortfolio_work/ePortfolio-tsgh/WebContent/WEB-INF/views/config/role.jsp:71
            // 的 `<a class="showEdit">名稱</a>`）。第一個連結欄就是「修改」的入口，
            // 所以 React 也把修改掛在它身上，而不是另外放一顆「修改」按鈕。
            if (!behaviorLinkKeys.has(columnKey)) {
              return inlineActions === null ? (
                value
              ) : (
                <>
                  {value}
                  {inlineActions}
                </>
              );
            }
            return (
              // 刻意用沒有 href 的 <a>，不是共用的 Link 元件：原系統就是
              // `<a class="showEdit" onclick="">名稱</a>`（沒有 href），而 Link 一定會
              // 輸出 href，href 在 normalize-dom 的屬性 allowlist 裡，加了就是一筆差異。
              // role／tabindex 都**不在** allowlist（normalize-dom.ts:298-330），
              // 所以補上鍵盤可操作性不會影響 structure 比對 —— 原系統這裡是不能用鍵盤
              // 操作的，這是刻意不照抄的無障礙缺陷（§24.2：a11y 不改變 1:1 UI）。
              // biome-ignore lint/a11y/useSemanticElements: 換成 <button> 會讓標籤名與原系統不同（原系統是 <a class="showEdit">），直接多一筆 structure 差異
              <a
                data-ui-id={`${pageId}-result-row-${key}-${toUiKey(columnKey)}-link`}
                className={`${LINK_TEXT_CLASS} cursor-pointer`}
                role="button"
                tabIndex={0}
                // biome-ignore lint/a11y/useValidAnchor: 原系統的 <a> 沒有 href（onclick 驅動）；href 在 normalize-dom 屬性 allowlist 內，補上就是一筆差異。鍵盤等效操作已由 tabIndex + onKeyDown 提供。
                onClick={() => {
                  // 批次 45（#10 B4-1）：linkNavigate 登錄頁的名稱連結＝導流（formList.js:113-122
                  // showFillForm → 填寫頁；站內導航鏡像 B19-2）。未登錄＝openEdit（既有）。
                  if (behavior?.linkNavigate !== undefined) {
                    navigate(behavior.linkNavigate(row));
                    return;
                  }
                  openEdit(row);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openEdit(row);
                  }
                }}
              >
                {value}
                {inlineActions}
              </a>
            );
          }
          // 操作欄：同一頁若有多個空表頭欄，只有第一個畫按鈕，其餘留空
          // （多畫等於憑空產生原系統沒有的動作）。
          return columnKey === primaryActionsKey ? rowActions(row, key) : null;
        }}
      />
    </DataTableShell>
  );

  return (
    // 模板只負責內容區：全站外框（ApplicationShell／Navbar／SidebarMenu／Breadcrumb）
    // 由路由層的 AppLayout 提供，模板再包一層會變成殼中殼。
    // 這個 <div> 是整頁的根節點，data-ui-id 就是 §10.1 說的 pageId 本身。
    <div data-ui-id={pageId}>
      {/*
        FC-4：原系統該頁的對話框（trigger tier A–D）。
        trigger 鈕帶 `data-modal-trigger`（FUNCTIONAL-CONTRACT.md D-3），
        對話框本身條件渲染 —— 唯讀 GET 抽不到是**正確結果**，React 側證據一律走
        `tests/react/extract-modals.spec.ts` 的互動擷取。
        三級（global／shared／unique）都由這裡畫：本專案沒有 AppLayout 這一層，
        而三級的內容全部來自同一份 legacy 實測資料，分開手抄只會多一次抄錯的機會。
      */}
      <LegacyContractModals
        pageId={pageId}
        // 批次 45（#11 B5-2，issue #11）：modal 欄位播種（學員勾選→students hidden）與
        // 送出驗證鏈 guard（leaveAnnual.jsp:136-165 逐條）；未登錄＝零影響。
        {...(behavior?.modalFieldSeed === undefined ? {} : { fieldSeed: behavior.modalFieldSeed(flowCtx) })}
        {...(behavior?.modalSubmitGuard === undefined
          ? {}
          : { submitGuard: (spec: LegacyModalSpec, payload: Record<string, string>) => behavior.modalSubmitGuard!(spec, payload, flowCtx) })}
      />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title={route.title}
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        {saved ? (
          <FormMessage uiId={`${pageId}-message-success`} tone="success">
            {resolveLegacyMessage(pageId, 'success')?.text ?? t('state.saveSuccess')}
          </FormMessage>
        ) : null}

        <SearchPanel
          uiId={`${pageId}-search`}
          busy={loading}
          // D-1：這顆「查詢」鈕真的會打 `dataSource.list(pageId, …)`（透過 useList），
          // 所以標 `list:<pageId>`；「清除」只清 state，不標（criteria C5-j）。
          searchEffect={`list:${pageId}`}
          // W11（批次 42，issue #40，裁決④案 B）：legacy <form> 的 action/method 鏡像。
          // 白名單制（legacy-form-specs）：現況只有 config-organization 命中（crud-list 頁）。
          // exactOptionalPropertyTypes：沒有 spec 時要「整個不傳」而不是傳 undefined。
          {...(formSpec === undefined ? {} : { formSpec })}
          /* 批次 43（#41）：查詢 form 頂部工具列（一次填寫多類；studyRecordFill.jsp:243-247）。 */
          topActions={behavior?.searchTopActions?.(flowCtx)}
          onSearch={() => {
            applySearch();
            // 原系統的「查詢」一定會重打端點；條件沒變時 queryKey 不變，
            // 只靠 state 變化不會重取，所以明確 reload()。
            // 幾個 state 更新在同一次 commit 內批次處理，effect 只會跑一次。
            reload();
          }}
          onReset={() => {
            resetSearch();
          }}
          /* 批次 44（#42）：查詢鈕後按鈕列（planFormList.jsp:709-721 形頁；legacy 查詢鈕在前）。 */
          tailActions={behavior?.searchTailActions?.(flowCtx)}
          extraActions={
            <>
              <Button
                uiId={`${pageId}-toolbar-add`}
                variant="primary"
                size="sm"
                type="button"
                // FC-4 D-3：這顆「新增」會開下面那個 `<ModalFrame uiId={pageId-form-dialog}>`
                // （openCreate → setEditing → open），依 D-3 第 1 點自報。
                // 這個對話框在**本批開工前就存在**（FC-1 就有），不是本批的工作量；
                // 標上去只是讓互動擷取抽得到它，criteria C5-h 要求把它逐群揭露。
                dataModalTrigger={
                  // 批次 10：editModal 登錄時，這顆開的是自訂 modal，D-3 自報對應其 uiId。
                  behavior?.editModalTriggerId ?? `${pageId}-form-dialog`
                }
                onClick={() => {
                  openCreate();
                }}
              >
                {/* 批次 1：行為層可覆寫新增鈕文字（department「新增一級部門」）；未登錄＝新增。 */}
                {behavior?.addLabel ?? t('action.add')}
              </Button>
              {/* 批次 1：帳號維護的批量工具（停用帳號/啟用帳號/清除被封鎖IP/人事同步/頭像）。 */}
              {behavior?.bulkActions?.(flowCtx) ?? null}
            </>
          }
        >
          <FormGrid uiId={`${pageId}-search-grid`} columns={2}>
            {/* W11（批次 42，issue #40，裁決④案 B）：契約欄位之前的行為層額外欄位
                （organization 組織類型；queryType 無 name 契約抽不到）。未登錄頁零影響。 */}
            {behavior?.queryExtraFields?.(
              flowCtx,
              (name, next) => {
                // legacy onchange="doQuery();"＝選了立刻查（organization.jsp:348；
                // :124 doQuery 送 type 參數）：寫草稿＋立即套用 filters＋重查。
                const merged = { ...draftFieldValues, [name]: next };
                setDraftFieldValues(merged);
                setFilters(toPayload(merged, contractFields, pageId));
                setPage(1);
                reload();
              },
            ) ?? null}
            {contractFields === undefined ? (
              <FormGridField uiId={`${pageId}-search-field-keyword`} label={t('search.keyword')}>
                <TextInput
                  uiId={`${pageId}-search-input-keyword`}
                  value={draftKeyword}
                  onChange={setDraftKeyword}
                  disabled={loading}
                />
              </FormGridField>
            ) : // R2（C2 findings F2）：這一頁有真正的欄位抽取資料時，關掉模板自帶的
            // 「關鍵字」欄位——legacy 沒有這個欄位，畫出來只會被 diff 演算法拿去跟
            // legacy 真正的欄位錯配（實測 1 筆「缺元素」換 5 筆「屬性不同／文字不同」，
            // 見 report-R2.md）。keyword／draftKeyword 這組 state 還留著：
            // DataTableShell 的「查看全部結果」/表內搜尋仍會用到 `onKeywordChange={applyKeyword}`，
            // 只是查詢區不再重複畫一顆關鍵字輸入框。
            null}
            {/*
              FC-3：有契約欄位就畫契約欄位（整個主體區的超集）。
              legacySearchGroupsFor() 的退路分支（只涵蓋第一個查詢 form 的舊路徑、
              查詢欄位舊元件）已刪除——legacy-fallback-dead-path.test.ts 證明全站
              156 頁沒有一頁需要它，那個舊元件從未被渲染過。
            */}
            {contractFields !== undefined ? (
              <LegacyContractFields
                pageId={pageId}
                fields={
                  // 批次 44（#42）：渲染排除（queryFieldExclusions；form-plan-form-list 的 ids
                  // 屬 printForm 表單＝批次 41 前例；契約面保留＝payload 照帶）。
                  behavior?.queryFieldExclusions === undefined
                    ? contractFields
                    : contractFields.filter((f) => !behavior.queryFieldExclusions!.includes(f.name))
                }
                {...(behavior?.queryInlineExtras === undefined
                  ? {}
                  : {
                      afterFieldNodes: behavior.queryInlineExtras(flowCtx, (name, next) => {
                        setDraftFieldValues((current) => ({ ...current, [name]: next }));
                      }),
                    })}
                values={draftFieldValues}
                onChange={(name, next) => {
                  setDraftFieldValues((current) => ({ ...current, [name]: next }));
                }}
                disabled={loading}
              />
            ) : null}
            {/* 批次 44（#42）：查詢 form 尾部欄位（comment selePrivate；onChange 只寫草稿，
                查詢按鈕才送出＝legacy 語意；與 queryExtraFields（onchange=doQuery 即查）不同）。 */}
            {behavior?.queryTailFields?.(
              flowCtx,
              (name, next) => {
                setDraftFieldValues((current) => ({ ...current, [name]: next }));
              },
            ) ?? null}
          </FormGrid>
          {/* 批次 39（GNL-005-c）：匯入區塊在查詢 form 內的頁（clazzList.jsp:152-170 在 queryForm 內；
              與批次 32 root 層不同）——behavior.importBlockInForm 渲染在 fields 後。 */}
          {behavior?.importBlockInForm?.(flowCtx) ?? null}
        </SearchPanel>

        {/* 批次 43（#41 GNL-005-e，裁決③）：legacy 查詢 form 右欄說明區（form 的 sibling；
            queryPage.jsp:211-218 等）。泛用文字地圖渲染；未命中＝零影響。 */}
        <LegacyAsideBlocks pageId={pageId} />

        {/* 批次 32（CFG-001，issue #33）：匯入區塊在 root 層（查詢 form 外、表格前，鏡像 legacy
            順序：查詢 form → 匯入區塊 → hr 分隔 → 表格；config-annual/organization raw-dom）。 */}
        {behavior?.importBlock?.(flowCtx) ?? null}

        {error !== null ? (
          <TemplateError
            uiId={`${pageId}-error`}
            error={error}
            onRetry={reload}
            pageId={pageId}
            {...(messageKeyOverride === undefined ? {} : { messageKeyOverride })}
          />
        ) : behavior?.tableArea !== undefined ? (
          behavior.tableArea(resultShell, flowCtx, rows)
        ) : (
          resultShell
        )}
      </ContentPanel>

      {/* 批次 10（B1-2）：行為層可提供自訂編輯 modal（帳號大 modal），取代下方通用 ModalFrame；
          未登錄 editModal 的頁走原路徑，零影響。 */}
      {behavior?.editModal !== undefined && editing !== null ? (
        behavior.editModal({
          pageId,
          mode: editing.mode,
          row: editing.row,
          // 批次 12（B1-4）：樹「新增子部門」的 parentId preset（openCreate preset）要傳給
          // 自訂 modal 判斷層級（department.jsp:62）。
          values: editing.values,
          onClose: () => {
            setEditing(null);
          },
          onClosed: reload,
        })
      ) : (
      <ModalFrame
        uiId={`${pageId}-form-dialog`}
        open={editing !== null}
        // 原系統標題是「新增／修改 + 功能名稱」（courseDepartmentCode.js:89,91），這裡照組。
        // 批次 1：行為層可覆寫（annual「新增年度/修改年度」、role「新增角色/修改角色」、
        // department「新增一級部門/修改子部門」…）。
        title={
          behavior?.modalTitle?.(editing?.mode ?? 'create', editing?.row ?? null, editing?.values) ??
            `${editing?.mode === 'create' ? t('action.add') : t('action.edit')}${route.title}`
        }
        size="lg"
        onClose={() => {
          setEditing(null);
        }}
        footer={
          <>
            <Button
              uiId={`${pageId}-form-dialog-cancel`}
              variant="default"
              size="sm"
              type="button"
              onClick={() => {
                setEditing(null);
              }}
            >
              {t('action.close')}
            </Button>
            {/* 批次 1：annual 停用列隱藏存檔（annual.jsp:56-63 `$('#btnSave').hide()`）。
                唯讀判定由行為層提供；未登錄行為層的頁恆為 false（原行為）。 */}
            {editing?.row !== null &&
            editing?.row !== undefined &&
            behavior?.modalReadOnly?.(editing.row) === true ? null : (
              <Button
                uiId={`${pageId}-form-dialog-save`}
                variant="primary"
                size="sm"
                type="button"
                onClick={() => {
                  if (editing !== null) {
                    void persist(editing);
                  }
                }}
              >
                {t('action.save')}
              </Button>
            )}
          </>
        }
      >
        <FormGrid uiId={`${pageId}-form-dialog-grid`} columns={2}>
          {(editing === null ? [] : formColumnsFor(editing.mode)).map((column) => {
            const fieldKey = toUiKey(column.key);
            const message = fieldErrors[column.key];
            return (
              <FormGridField
                key={column.key}
                uiId={`${pageId}-form-dialog-field-${fieldKey}`}
                label={column.label}
                span={column.kind === 'text' ? 2 : 1}
                {...(message === undefined ? {} : { error: message })}
              >
                <FieldControl
                  uiId={`${pageId}-form-dialog-input-${fieldKey}`}
                  column={column}
                  value={editing?.values[column.key] ?? ''}
                  invalid={message !== undefined}
                  // 批次 1：annual 停用列的欄位 disabled（annual.jsp:58-62；原系統只鎖
                  // 年度/排序，雛形整表鎖，差異記進進度檔）。
                  // crud-fix（2026-10-04）：行為層的逐欄鎖（config-job-title 修改 modal 的
                  // 醫院欄 legacy 回填後 disabled，jobTitle.jsp showModal）。
                  readOnly={
                    (editing?.row != null && (behavior?.modalReadOnly?.(editing.row) ?? false)) ||
                    (behavior?.modalFieldDisabled?.(column.key, editing?.mode ?? 'create') ?? false)
                  }
                  // crud-fix（P35 反轉）：id（編號）欄解除唯讀的例外（config-job-title 職稱
                  // 代碼欄，jobTitle.jsp showModal 新增/修改皆可填；未登錄頁＝原行為唯讀）。
                  allowIdEdit={behavior?.modalIdEditable?.(column.key) === true}
                  onChange={(next) => {
                    setEditing((current) =>
                      current === null
                        ? current
                        : { ...current, values: { ...current.values, [column.key]: next } },
                    );
                  }}
                />
              </FormGridField>
            );
          })}
        </FormGrid>
        {/* FC-5 R3：欄位數裝不下的 legacy validation 訊息，補在表單下方。 */}
        <ExtraValidationMessages uiId={`${pageId}-form-dialog`} messages={extraErrors} />
      </ModalFrame>
      )}
      {/* crud-fix（2026-10-04）：存檔成功後的第二段 modal（qb 兩段式誠實殼，postSaveModal）。
          內容＝唯讀說明殼＋關閉鈕（真題目維護＝#19 範疇）；UI id 依 `${pageId}-init-import-modal`
          命名（對應 legacy questionBank.jsp 的 #initImportModal）。 */}
      <ModalFrame
        uiId={`${pageId}-init-import-modal`}
        open={postSave !== null}
        title={postSave?.title ?? ''}
        onClose={() => setPostSave(null)}
        footer={
          <Button
            uiId={`${pageId}-init-import-modal-close`}
            variant="default"
            size="sm"
            type="button"
            onClick={() => {
              setPostSave(null);
            }}
          >
            {t('action.close')}
          </Button>
        }
      >
        <p data-ui-id={`${pageId}-init-import-modal-note`} className="text-sm text-ink">
          {postSave?.note ?? ''}
        </p>
      </ModalFrame>
    </div>
  );
}
